import { useState } from "react";
import type { WidgetRollup } from "@models/Rollup";
import type { QuestionDetail } from "@models/Dataset";
import type {
  ScorecardQuestionRow,
  ScorecardSourceQuestionKind,
} from "@models/Scorecard";
import type { GridMode } from "@components/charts/scorecard-config/components/QuestionRowEditor/GridValueOrRollupSelector";

// Same rules RollupBuilder.tsx enforces for its own name input — mirrored
// here (same as BarChartConfigDrawer.tsx's isFocusValid precedent) so an
// invalid rollup name blocks Save, not just shows an inline error.
const ALLOWED_ROLLUP_NAME_CHARS = /^[a-zA-Z0-9 _-]*$/;
const MAX_ROLLUP_NAME_LENGTH = 100;

/**
 * Owns a Single grid Metric Row's metric-selection state — one Grid Value
 * or a SUM_SELECTED rollup of >=2 Grid Values, mutually exclusive. Extracted
 * from MetricRowBuilder.tsx to keep it under the file-size limit.
 */
export function useGridMetricSelection(editingRow?: ScorecardQuestionRow | null) {
  const [selectedGridValueId, setSelectedGridValueId] = useState(
    editingRow?.valueFocus?.selectedValueId ?? "",
  );
  const [selectedGridValueLabel, setSelectedGridValueLabel] = useState(
    editingRow?.metricValueLabel ?? "",
  );
  const [selectedGridValueCode, setSelectedGridValueCode] = useState(
    editingRow?.metricValueCode ?? "",
  );
  const [gridMode, setGridModeState] = useState<GridMode>(
    editingRow?.rollup ? "rollup" : "value",
  );
  const [rollupName, setRollupName] = useState(editingRow?.rollup?.name ?? "");
  const [rollupSelectedCodes, setRollupSelectedCodes] = useState<string[]>([]);

  const resetForNewQuestion = () => {
    setSelectedGridValueId("");
    setSelectedGridValueLabel("");
    setSelectedGridValueCode("");
    setGridModeState("value");
    setRollupName("");
    setRollupSelectedCodes([]);
  };

  /** Reverse-maps the saved stable Value.ids to RollupBuilder's code-keyed
   *  selection — only resolvable once the question's value list has loaded. */
  const restoreRollupCodes = (values: QuestionDetail["values"]) => {
    if (!editingRow?.rollupValueIds) return;
    const codes = editingRow.rollupValueIds
      .map((id) => values.find((v) => v.id === id))
      .filter((v): v is (typeof values)[number] => !!v)
      .map((v) => String(v.value));
    setRollupSelectedCodes(codes);
  };

  /** Returns the resolved display label so the caller can decide whether to
   *  prepopulate the row's own label field (only when untouched). */
  const handleGridValueChange = (
    valueId: string,
    values: QuestionDetail["values"],
  ): string => {
    setSelectedGridValueId(valueId);
    const value = values.find((v) => v.id === valueId);
    const valueLabel = value ? value.title || value.label : "";
    setSelectedGridValueLabel(valueLabel);
    setSelectedGridValueCode(value ? String(value.value) : "");
    return valueLabel;
  };

  // Switching mode clears the other mode's fields so a stale value/rollup
  // selection never lingers into a save under the newly-chosen mode.
  const handleGridModeChange = (mode: GridMode) => {
    setGridModeState(mode);
    if (mode === "value") {
      setRollupName("");
      setRollupSelectedCodes([]);
    } else {
      setSelectedGridValueId("");
      setSelectedGridValueLabel("");
      setSelectedGridValueCode("");
    }
  };

  const isRollupValid = (values: QuestionDetail["values"]): boolean => {
    if (gridMode !== "rollup") return true;
    const trimmed = rollupName.trim();
    if (
      !trimmed ||
      trimmed.length > MAX_ROLLUP_NAME_LENGTH ||
      !ALLOWED_ROLLUP_NAME_CHARS.test(trimmed)
    ) {
      return false;
    }
    const lower = trimmed.toLowerCase();
    const hasConflict = values.some(
      (v) =>
        v.title?.trim().toLowerCase() === lower ||
        v.rowtitle?.trim().toLowerCase() === lower ||
        v.label?.trim().toLowerCase() === lower,
    );
    if (hasConflict) return false;
    return rollupSelectedCodes.length >= 2;
  };

  const isGridReady = (sourceQuestionKind: ScorecardSourceQuestionKind | null): boolean =>
    sourceQuestionKind !== "single_grid" ||
    (gridMode === "value" ? !!selectedGridValueId : rollupSelectedCodes.length >= 2);

  /** Builds the mutually-exclusive save-time fields for a Single grid Metric
   *  Row's metric selection. `rollup.sourceCategoryLabels` is the raw codes
   *  as-is — no remapping needed, since that's already the exact shape
   *  BarChart's own `focusRollup` expects for a value-axis rollup. */
  const buildSaveFields = (
    questionDetail: QuestionDetail,
    sourceQuestionKind: ScorecardSourceQuestionKind,
    existingRollupId?: string,
  ) => {
    const isRollupMode = sourceQuestionKind === "single_grid" && gridMode === "rollup";

    const rollup: WidgetRollup | null = isRollupMode
      ? {
          id: existingRollupId ?? crypto.randomUUID(),
          name: rollupName.trim(),
          calculationType: "SUM_SELECTED",
          sourceCategoryLabels: rollupSelectedCodes,
        }
      : null;
    const rollupValueIds: string[] | null = isRollupMode
      ? rollupSelectedCodes
          .map((code) => questionDetail.values.find((v) => String(v.value) === code)?.id)
          .filter((id): id is string => !!id)
      : null;

    return {
      valueFocus:
        sourceQuestionKind === "single_grid" && !isRollupMode
          ? { focusType: "value" as const, selectedValueId: selectedGridValueId }
          : null,
      metricValueLabel:
        sourceQuestionKind === "single_grid" && !isRollupMode
          ? selectedGridValueLabel
          : null,
      metricValueCode:
        sourceQuestionKind === "single_grid" && !isRollupMode
          ? selectedGridValueCode
          : null,
      rollup,
      rollupValueIds,
    };
  };

  return {
    selectedGridValueId,
    selectedGridValueLabel,
    gridMode,
    rollupName,
    rollupSelectedCodes,
    setRollupName,
    setRollupSelectedCodes,
    resetForNewQuestion,
    restoreRollupCodes,
    handleGridValueChange,
    handleGridModeChange,
    isRollupValid,
    isGridReady,
    buildSaveFields,
  };
}
